// Single source of truth for the therapist navigation (Phase 2 sidebar shell).
//
// The 13 routes are grouped into three semantic clusters so the rail scales by
// adding rows, not by competing for a fixed horizontal budget. Lifted out of
// TherapistHeader.js so the sidebar and the legacy AppBar can share intent.

import React from 'react';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Route as RouteIcon,
  Forum as ForumIcon,
  RecordVoiceOver as VoiceIcon,
  AudioFile as AudioIcon,
  PlaylistAddCheck as ReviewsIcon,
  Shield as ShieldIcon,
  Science as PreSessionIcon,
  School as SchoolIcon,
  SmartToy as TrainingClientsIcon,
  Tune as TuneIcon,
  LibraryMusic as VoiceWhitelistIcon,
} from '@mui/icons-material';

// Layout width constants (kept here so the Sidebar and the App content offset
// stay in sync). Rail = collapsed icon-only; full = labelled.
export const SIDEBAR_WIDTH = 232;
export const SIDEBAR_RAIL_WIDTH = 64;
export const TOPBAR_HEIGHT = 56;

export const NAV_GROUPS = [
  {
    id: 'clinical',
    label: 'Clinical',
    items: [
      { id: 'dashboard',        label: 'Dashboard',          icon: <DashboardIcon />,       path: '/therapist/dashboard' },
      { id: 'clients',          label: 'Clients',            icon: <PeopleIcon />,          path: '/therapist/dashboard?tab=clients' },
      { id: 'pathways',         label: 'Treatment Pathways', icon: <RouteIcon />,           path: '/therapist/dashboard?tab=pathways' },
      { id: 'consultations',    label: 'Consultations',      icon: <ForumIcon />,           path: '/therapist/dashboard?tab=consultations' },
      { id: 'client-reviews',   label: 'Client Reviews',     icon: <ReviewsIcon />,         path: '/therapist/reviews' },
    ],
  },
  {
    id: 'config',
    label: 'Config',
    items: [
      { id: 'voice-settings',       label: 'Voice Settings',      icon: <VoiceIcon />,          path: '/therapist/dashboard?tab=voice-settings' },
      { id: 'client-voice-options', label: 'Client Voices',       icon: <VoiceWhitelistIcon />, path: '/therapist/dashboard?tab=client-voice-options' },
      { id: 'audio-exports',        label: 'Audio Exports',       icon: <AudioIcon />,          path: '/therapist/dashboard?tab=audio-exports' },
      { id: 'boundary-monitoring',  label: 'Boundary Monitoring', icon: <ShieldIcon />,         path: '/therapist/dashboard?tab=boundary-monitoring' },
      { id: 'client-defaults',      label: 'Client Defaults',     icon: <TuneIcon />,           path: '/therapist/dashboard?tab=client-defaults' },
    ],
  },
  {
    id: 'training',
    label: 'Training',
    items: [
      { id: 'practice-settings', label: 'Practice Settings', icon: <TuneIcon />,             path: '/therapist/practice-settings' },
      { id: 'simulation-lab',    label: 'Simulation Lab',    icon: <SchoolIcon />,           path: '/therapist/simulation-lab' },
      { id: 'pre-session',       label: 'Pre-Session',       icon: <PreSessionIcon />,       path: '/therapist/pre-session' },
      { id: 'training-clients',  label: 'Training Clients',  icon: <TrainingClientsIcon />,  path: '/therapist/dashboard?tab=training-clients' },
    ],
  },
];

// Flat list of all items (e.g. for the mobile drawer or search).
export const ALL_NAV_ITEMS = NAV_GROUPS.flatMap((g) => g.items);

// Add a cache-busting timestamp to the clients route (mirrors legacy behaviour:
// forces a refresh and clears the selected client).
export const getNavPath = (item) =>
  item.id === 'clients' ? `/therapist/dashboard?tab=clients&t=${Date.now()}` : item.path;

// Resolve the active nav item id from the current location (pathname + ?tab).
export const getActiveNavId = (location) => {
  const path = location.pathname || '';
  const tab = new URLSearchParams(location.search || '').get('tab');

  if (tab === 'clients') return 'clients';
  if (tab === 'pathways') return 'pathways';
  if (tab === 'consultations') return 'consultations';
  if (tab === 'voice-settings') return 'voice-settings';
  if (tab === 'client-voice-options') return 'client-voice-options';
  if (tab === 'client-defaults') return 'client-defaults';
  if (tab === 'audio-exports') return 'audio-exports';
  if (tab === 'boundary-monitoring') return 'boundary-monitoring';
  if (tab === 'training-clients') return 'training-clients';
  if (path.includes('/therapist/reviews')) return 'client-reviews';
  if (path.includes('/therapist/practice-settings')) return 'practice-settings';
  if (path.includes('/therapist/pre-session')) return 'pre-session';
  if (path.includes('/therapist/simulation-lab')) return 'simulation-lab';
  if (path.includes('/therapist/dashboard') && !tab) return 'dashboard';
  return '';
};
